import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
// form
import { Controller, useFormContext } from 'react-hook-form';
// @mui
import { InputAdornment, TextField } from '@mui/material';
import { CurrencyInput } from 'react-currency-mask';

RHFCurrencyTextField.propTypes = {
  name: PropTypes.string,
  helperText: PropTypes.node,
  currency: PropTypes.string,
  emptyValue: PropTypes.string,
  rules: PropTypes.object,
};

/**
 * BRL e USD: react-currency-mask (mesmo fluxo de dígitos / centavos).
 * USD: en-US + símbolo no Intl (hideSymbol false — slice(3) da lib quebra "$5.00").
 * BRL: pt-BR + hideSymbol + adornment R$.
 */
function maskValueForCurrencyInput(fieldValue, emptyValue) {
  if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
    return emptyValue;
  }
  if (Object.is(fieldValue, 0)) {
    return '0.00';
  }
  return fieldValue;
}

function commitValueFromMask(raw, emptyValue) {
  if (raw === undefined || raw === null) {
    return emptyValue;
  }
  return raw;
}

const capCurrencyValue = (value, max) => {
  const numericMax = Number(max);
  const numericValue = Number(value);

  if (Number.isFinite(numericMax) && Number.isFinite(numericValue) && numericValue > numericMax) {
    return numericMax;
  }

  return value;
};

export default function RHFCurrencyTextField({
  name,
  helperText,
  currency = 'BRL',
  emptyValue = '0.00',
  rules,
  ...other
}) {
  const { control } = useFormContext();
  const { placeholder: placeholderFromProps, focus, ...restOther } = other;
  const code = (currency || 'BRL').toString().toUpperCase();
  const isUsd = code === 'USD';
  const placeholder =
    placeholderFromProps ?? (isUsd ? '$ 0.00' : 'R$ 0,00');

  const inputRef = useRef();

  useEffect(() => {
    if (focus) {
      inputRef.current?.focus();
    }
  }, [focus]);

  const locale = isUsd ? 'en-US' : 'pt-BR';
  const currencyCode = isUsd ? 'USD' : 'BRL';
  /** USD: não usar hideSymbol — a lib faz slice(3) e corrompe "$5.00" / milhares. */
  const hideSymbol = !isUsd;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <CurrencyInput
          {...restOther}
          hideSymbol={hideSymbol}
          currency={currencyCode}
          locale={locale}
          placeholder={placeholder}
          max={restOther.max}
          value={maskValueForCurrencyInput(field.value, emptyValue)}
          onChangeValue={(_event, originalValue) => {
            const cappedValue = capCurrencyValue(originalValue, restOther.max);

            if (restOther?.onChange) {
              restOther.onChange(cappedValue);
            }
            field.onChange(commitValueFromMask(cappedValue, emptyValue));
          }}
          InputElement={
            <TextField
              error={!!error}
              inputRef={(el) => {
                inputRef.current = el;
                if (typeof field.ref === 'function') {
                  field.ref(el);
                } else if (field.ref) {
                  field.ref.current = el;
                }
              }}
              helperText={error ? error?.message : helperText}
              placeholder={placeholder}
              InputProps={{
                ...(restOther.InputProps || {}),
                ...(hideSymbol
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">R$</InputAdornment>
                      ),
                    }
                  : {}),
              }}
            />
          }
        />
      )}
    />
  );
}
